<template>
  <modal
      :show.sync="show"
      class="modal-primary"
      :show-close="true"
      headerClasses="justify-content-center"
    >
      <div slot="header" class>
        <i class="fa fa-download mr-2"></i> Export terminals
      </div>

      <form>
        <div class="form-group">
          <label for="lastConnectionAtPreset">Last connection date</label>
          <select
            v-model="options.lastConnectionAtPreset"
            class="form-control"
            name="lastConnectionAtPreset"
            id="lastConnectionAtPreset"
          >
            <option value="custom">Custom date range</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This week</option>
            <option value="last_week">Last week</option>
            <option value="previous_7_days">Last 7 days</option>
            <option value="this_month">This month</option>
            <option value="last_month">Last month</option>
            <option value="previous_30_days">Last 30 days</option>
            <option value="this_year">This year</option>
            <option value="last_year">Last year</option>
            <option value="previous_365_days">Last 365 days</option>
            <option value="all_time">All time</option>
          </select>
        </div>

        <div v-if="options.lastConnectionAtPreset === 'custom'">
          <div class="form-group">
            <label class="form-label" for="lastConnectionAtFromDate">From</label>
            <el-date-picker
              v-model="options.lastConnectionAtFromDate"
              type="date"
              placeholder="Start date"
              name="lastConnectionAtFromDate"
              id="lastConnectionAtFromDate"
              :picker-options="exportDatePickerFromOptions"
            ></el-date-picker>
          </div>

          <div class="form-group">
            <label class="form-label" for="lastConnectionAtToDate">To</label>
            <el-date-picker
              v-model="options.lastConnectionAtToDate"
              type="date"
              placeholder="End date"
              name="lastConnectionAtToDate"
              id="lastConnectionAtToDate"
              :picker-options="exportDatePickerToOptions"
            ></el-date-picker>
          </div>

          <span v-if="exportDatesError" class="text-danger"
            >Warning: {{ exportDatesError }}</span
          >
        </div>

        <!-- TODO: column options -->
      </form>

      <div class="alert alert-warning" v-if="exportError">
        Error exporting terminals: {{ exportError }}
      </div>

      <template slot="footer">
        <div class="left-side">
          <p-button
            type="default"
            link
            @click="show = false"
          >
            Cancel &nbsp;
            <i class="fa fa-times"></i>
          </p-button>
        </div>
        <div class="divider"></div>
        <div class="right-side">
          <p-button
            type="primary"
            link
            :disabled="exporting"
            @click="exportTerminals()"
          >
            <span v-if="!exporting">
              Export
              <i class="fa fa-check ml-2"></i>
            </span>
            <span v-if="exporting">
              Please wait &nbsp;
              <i class="fa fa-spin fa-circle-o-notch"></i>
            </span>
          </p-button>
        </div>
      </template>
    </modal>
</template>

<script>
import _ from "lodash";
import { whilst } from "async-es";
import moment from "moment";
import { DatePicker } from "element-ui";

import { Button, Modal } from "src/components/UIComponents";

export default {
  name: "TerminalsExportModal",
  components: {
    [Button.name]: Button,
    [DatePicker.name]: DatePicker,
    Modal
  },
  data() {
    return {
      show: false,
      exporting: false,
      exportError: null,
      options: {
        lastConnectionAtFromDate: null,
        lastConnectionAtToDate: null,
        lastConnectionAtPreset: "last_month",
        columns: ["id", "serialNumber", "reference", "lastConnectionAt"]
      },
      exportDatePickerFromOptions: {
        disabledDate(date) {
          // date must be today or earlier
          return date > new Date();
        }
      },
      exportDatePickerToOptions: {
        disabledDate(date) {
          // date must be today or earlier
          if (date < new Date()) {
            return false;
          }
        }
      }
    };
  },
  computed: {
    exportDatesError: function() {
      if (this.options.lastConnectionAtFromDate > new Date()) {
        return "Start date is after current date";
      }

      if (
        this.options.lastConnectionAtFromDate &&
        this.options.lastConnectionAtToDate
      ) {
        if (
          this.options.lastConnectionAtToDate <
          this.options.lastConnectionAtFromDate
        ) {
          return "End date is before Start date";
        }
      }
    }
  },
  methods: {
    showModal: function() {
      this.show = true;
    },
    exportTerminals: function() {
      const instance = this;
      instance.exporting = true;

      // build query
      let query = "?";

      let startDate = null;
      let endDate = null;
      if (instance.options.lastConnectionAtPreset === "custom") {
        startDate = moment(instance.options.lastConnectionAtFromDate).startOf(
          "day"
        );

        endDate = moment(instance.options.lastConnectionAtToDate).endOf("day");
      } else if (instance.options.lastConnectionAtPreset === "today") {
        startDate = moment().startOf("day");

        endDate = moment().endOf("day");
      } else if (instance.options.lastConnectionAtPreset === "yesterday") {
        startDate = moment()
          .subtract(1, "day")
          .startOf("day");

        endDate = moment()
          .subtract(1, "day")
          .endOf("day");
      } else if (instance.options.lastConnectionAtPreset === "this_week") {
        startDate = moment().startOf("week");

        endDate = moment();
      } else if (instance.options.lastConnectionAtPreset === "last_week") {
        startDate = moment()
          .subtract(1, "week")
          .startOf("week");

        endDate = moment()
          .subtract(1, "week")
          .endOf("week");
      } else if (
        instance.options.lastConnectionAtPreset === "previous_7_days"
      ) {
        startDate = moment()
          .subtract(7, "days")
          .startOf("day");

        endDate = moment();
      } else if (instance.options.lastConnectionAtPreset === "this_month") {
        startDate = moment().startOf("month");

        endDate = moment();
      } else if (instance.options.lastConnectionAtPreset === "last_month") {
        startDate = moment()
          .subtract(1, "month")
          .startOf("month");

        endDate = moment()
          .subtract(1, "month")
          .endOf("month");
      } else if (
        instance.options.lastConnectionAtPreset === "previous_30_days"
      ) {
        startDate = moment()
          .subtract(30, "days")
          .startOf("day");

        endDate = moment();
      } else if (instance.options.lastConnectionAtPreset === "this_year") {
        startDate = moment().startOf("year");

        endDate = moment();
      } else if (instance.options.lastConnectionAtPreset === "last_year") {
        startDate = moment()
          .subtract(1, "year")
          .startOf("year");

        endDate = moment()
          .subtract(1, "year")
          .endOf("year");
      } else if (
        instance.options.lastConnectionAtPreset === "previous_365_days"
      ) {
        startDate = moment()
          .subtract(365, "days")
          .startOf("day");

        endDate = moment();
      }

      // set to query string
      if (
        instance.options.lastConnectionAtPreset !== "all_time" &&
        startDate &&
        endDate
      ) {
        query += `lastConnectionAt[$gte]=${startDate.toISOString()}`;

        query += `&lastConnectionAt[$lt]=${endDate.toISOString()}`;
      }

      let terminals = [];

      let totalAvailable = null;

      let offset = 0;
      const limit = 100;
      whilst(
        function test(cb) {
          if (instance.show === false) {
            // user cancelled the operation
            return cb(null, false);
          }

          if (totalAvailable === null) {
            // first run
            return cb(null, true);
          } else {
            return cb(null, terminals.length < totalAvailable);
          }
        },
        function iter(callback) {
          instance.axios
            .get(`v1/terminals${query}&offset=${offset}&limit=${limit}`)
            .then(response => {
              terminals = terminals.concat(response.data);

              if (totalAvailable === null) {
                totalAvailable = parseInt(response.headers["total-count"]);
              }

              offset += limit;

              return callback(null);
            })
            .catch(err => {
              // error - opps
              console.error("Error: ", err); // TODO: set to error
              return callback(err);
            });
        },
        function(err, n) {
          if (err) {
            // set err to error
          } else {
            // completion without error
            instance.exporting = false;

            if (instance.show === false) {
              // user cancelled the operation, do nothing (could notify: cancelled)
            } else {
              // manipulate data (select and format rows)
              const dateFormat = "YYYY-MM-DDTHH:mm:ss";
              const formattedTerminals = terminals.map(terminal => {
                const result = [
                  terminal.id,
                  terminal.serialNumber,
                  `${terminal.manufacturer} ${terminal.model}`,
                  terminal.reference,
                  terminal.customReference,
                  terminal.lastConnectionAt
                    ? moment(terminal.lastConnectionAt).format(dateFormat)
                    : null // format in local time
                ];

                // allocation ID
                if (terminal.currentAllocation) {
                  if (terminal.currentAllocation.submerchantId) {
                    result.push(terminal.currentAllocation.submerchantId);
                  } else if (terminal.currentAllocation.depositoryId) {
                    result.push(terminal.currentAllocation.depositoryId);
                  }
                } else {
                  result.push(null);
                }

                // allocation date
                if (
                  terminal.currentAllocation &&
                  terminal.currentAllocation.createdAt
                ) {
                  result.push(
                    moment(terminal.currentAllocation.createdAt).format(
                      dateFormat
                    )
                  );
                } else {
                  result.push(null);
                }

                return result;
              });

              // write to CSV
              const csv = instance.$papa.unparse(
                {
                  fields: [
                    "ID",
                    "Serial Number",
                    "Model",
                    "Reference",
                    "Custom Reference",
                    "Last Connection Date",
                    "Allocation",
                    "Allocation Date"
                  ],
                  data: formattedTerminals
                },
                {
                  delimiter: ",",
                  quotes: [
                    true, // ID
                    true, // serial number
                    true, // model
                    true, // reference
                    true, // custom reference
                    false, // last connection
                    true, // allocation
                    false // allocation date
                  ]
                }
              );
              // Trigger "download"
              instance.$papa.download(csv, "Terminals Export");

              // Show swal alert
              instance.$swal({
                icon: "success",
                title: "Export complete",
                text: `Your file download has started automatically.`
              });

              // close modal
              instance.show = false;
            }
          }
        }
      );
    }
  }
};
</script>
